/**
 * Corregir la cédula de un cliente en todo su historial (solo AdmG).
 *
 * Flujo: se digita la cédula nueva → "Revisar" muestra qué cambia
 * (/api/cambiocedula_previa: financiamientos, cuotas, recibos, iniciales,
 * foto, ficha, ventas donde es fiador) → confirmar aplica la corrección en
 * una sola transacción (/api/cambiocedula_aplicar). Si la cédula nueva ya es
 * de la misma persona se ofrece unificar los expedientes; si es de otra, se
 * bloquea. Abajo, el historial de correcciones del documento con la opción
 * de revertir (/api/cambiocedula_revertir). Ver app/cambiocedula.py.
 */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import { useGlobalMDAlert } from "context";
import { FP, DocumentoField } from "components/financepro";
import { useApi } from "lib/api";
import { tipoDocumento, documentoValido, formatDocumentoAuto } from "lib/documento";

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };

export default function CorregirCedulaDialog({ open, onClose, cedula, onCorregida }) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const [tipo, setTipo] = useState("cedula");
  const [nueva, setNueva] = useState("");
  const [incluirFiador, setIncluirFiador] = useState(true);
  const [previa, setPrevia] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  const [historial, setHistorial] = useState([]);

  const cargarHistorial = () =>
    post("/api/cambiocedula_historial", { cedula }).then(({ ok, data }) => {
      if (ok) setHistorial(data.historial || []);
    });

  useEffect(() => {
    if (!open) return;
    setTipo(tipoDocumento(cedula) || "cedula");
    setNueva("");
    setPrevia(null);
    setIncluirFiador(true);
    cargarHistorial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cedula]);

  const revisar = async () => {
    if (!documentoValido(nueva, tipo)) {
      showAlert({ message: "La cédula nueva no está completa", color: "warning" });
      return;
    }
    setOcupado(true);
    try {
      const { ok, data } = await post("/api/cambiocedula_previa", {
        cedula,
        cedulanueva: nueva,
        incluirfiador: incluirFiador,
      });
      if (ok) setPrevia(data);
      else {
        setPrevia(null);
        showAlert({ message: data.Error || "No se pudo revisar la cédula", color: "error" });
      }
    } finally {
      setOcupado(false);
    }
  };

  const aplicar = async () => {
    setOcupado(true);
    try {
      const { ok, data } = await post("/api/cambiocedula_aplicar", {
        cedula,
        cedulanueva: nueva,
        incluirfiador: incluirFiador,
        unificar: previa?.modo === "unificar",
      });
      if (ok) {
        showAlert({ message: data.mensajeok, color: "success" });
        onCorregida(data.nueva);
      } else {
        showAlert({ message: data.Error || "No se pudo corregir la cédula", color: "error" });
      }
    } finally {
      setOcupado(false);
    }
  };

  const revertir = async (h) => {
    setOcupado(true);
    try {
      const { ok, data } = await post("/api/cambiocedula_revertir", { id: h.id });
      if (ok) {
        showAlert({ message: data.mensajeok, color: "success" });
        onCorregida(data.cedula);
      } else {
        showAlert({ message: data.Error || "No se pudo revertir", color: "error" });
      }
    } finally {
      setOcupado(false);
    }
  };

  const bloqueado = previa?.modo === "bloqueado";
  const unificar = previa?.modo === "unificar";

  return (
    <Dialog
      open={open}
      onClose={ocupado ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1.2 }}>
        <BadgeOutlinedIcon sx={{ color: FP.color.navy }} />
        Corregir cédula del cliente
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: "0.86rem", color: FP.color.textSecondary, mb: 2 }}>
          Cédula actual: <b>{formatDocumentoAuto(cedula)}</b>. Se cambia en todos sus
          financiamientos, cuotas, recibos, iniciales, solicitudes y su ficha. Los comprobantes
          fiscales ya emitidos no se modifican.
        </Typography>
        <DocumentoField
          label="Cédula nueva"
          value={nueva}
          tipo={tipo}
          onChangeValor={(v) => {
            setNueva(v);
            setPrevia(null);
          }}
          onChangeTipo={(t) => {
            setTipo(t);
            setPrevia(null);
          }}
        />
        <FormControlLabel
          sx={{ mt: 1 }}
          control={
            <Checkbox
              checked={incluirFiador}
              onChange={(e) => {
                setIncluirFiador(e.target.checked);
                setPrevia(null);
              }}
            />
          }
          label="Corregirla también donde figura como fiador de otros clientes"
        />

        {previa && (
          <Box sx={{ mt: 1.5 }}>
            {bloqueado && <Alert severity="error">{previa.motivo}</Alert>}
            {unificar && (
              <Alert severity="warning" sx={{ mb: 1.5 }}>
                La cédula {previa.nueva} ya existe para la misma persona (
                {(previa.nombresnueva || []).map((n) => n.join(" ")).join(", ") || "sin nombre"}
                {previa.prestamosnueva?.length
                  ? `, ${previa.prestamosnueva.length} financiamiento(s)`
                  : ""}
                ). Al confirmar se <b>unifican los expedientes</b>: la foto y la ficha se fusionan
                sin pisar lo que ya tiene.
              </Alert>
            )}
            {!bloqueado && (
              <>
                <Typography sx={{ fontWeight: 800, fontSize: "0.86rem", mb: 0.8 }}>
                  Se cambiará la cédula a {previa.nueva} en:
                </Typography>
                <Stack spacing={0.4}>
                  {previa.conteos.map((x) => (
                    <Stack
                      key={`${x.tabla}.${x.columna}`}
                      direction="row"
                      justifyContent="space-between"
                      sx={{
                        px: 1.2,
                        py: 0.5,
                        borderRadius: 1.4,
                        border: `1px solid ${FP.color.border}`,
                        fontSize: "0.84rem",
                      }}
                    >
                      <span>{x.etiqueta}</span>
                      <b>{x.filas}</b>
                    </Stack>
                  ))}
                </Stack>
                {previa.prestamos.length > 0 && (
                  <Typography sx={{ fontSize: "0.8rem", color: FP.color.textSecondary, mt: 1 }}>
                    Financiamientos: {previa.prestamos.map((p) => `#${p.noprest}`).join(", ")}
                    {previa.fiador.length > 0 &&
                      ` · Fiador en: ${previa.fiador.map((p) => `#${p.noprest}`).join(", ")}`}
                  </Typography>
                )}
              </>
            )}
          </Box>
        )}

        {historial.length > 0 && (
          <Box sx={{ mt: 2.4 }}>
            <Typography sx={{ fontWeight: 800, fontSize: "0.82rem", mb: 0.6 }}>
              Correcciones anteriores
            </Typography>
            <Stack spacing={0.5}>
              {historial.map((h) => (
                <Stack
                  key={h.id}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    px: 1.2,
                    py: 0.5,
                    borderRadius: 1.4,
                    bgcolor: FP.color.surfaceAlt,
                    fontSize: "0.8rem",
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    #{h.id} · {h.fecha} · {h.cedula_ant} → {h.cedula_nueva}
                    {Number(h.unificado) ? " (unificó)" : ""} · {h.usuario}
                    {h.revertido ? ` · revertida ${h.revertido}` : ""}
                  </Box>
                  {!h.revertido && String(h.alcance).startsWith("cliente") && (
                    <Button
                      size="small"
                      disabled={ocupado}
                      onClick={() => revertir(h)}
                      sx={{ ...btnBase, color: FP.color.danger }}
                    >
                      Revertir
                    </Button>
                  )}
                </Stack>
              ))}
            </Stack>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          onClick={onClose}
          disabled={ocupado}
          sx={{ ...btnBase, color: FP.color.textSecondary }}
        >
          Cancelar
        </Button>
        {!previa || bloqueado ? (
          <Button
            onClick={revisar}
            disabled={ocupado || !nueva}
            startIcon={ocupado ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              ...btnBase,
              bgcolor: FP.color.navy,
              color: "#fff",
              "&:hover": { bgcolor: FP.color.navySoft },
            }}
          >
            Revisar
          </Button>
        ) : (
          <Button
            onClick={aplicar}
            disabled={ocupado}
            startIcon={ocupado ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              ...btnBase,
              bgcolor: unificar ? FP.color.danger : FP.color.navy,
              color: "#fff",
              "&:hover": { bgcolor: unificar ? FP.color.danger : FP.color.navySoft },
            }}
          >
            {unificar ? "Unificar expedientes" : "Corregir cédula"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

CorregirCedulaDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  cedula: PropTypes.string.isRequired,
  onCorregida: PropTypes.func.isRequired,
};
